import Icon from '@components/utils/Icon'
import cn from '@components/utils/cn'

import timeFormatter from '@frontend/utils/timeFormatter'
import { scopedTranslation } from '@utils/I18n'

import { CoverageMeter } from './CoverageMeter'
import { SlotChips } from './SlotChips'
import { RemovedRow } from './RemovedRow'
import { ShiftStatusBadge } from './ShiftStatusBadge'
import { AssignOnlyBadge } from './AssignOnlyBadge'
import { UndoRemovalButton } from './UndoRemovalButton'
import { PendingRemovalAssignees } from './PendingRemovalAssignees'
import { SelectionCheckbox } from './SelectionCheckbox'
import { useShiftsStore } from './stores/useShiftsStore'

import { ShiftActionsMenu } from '../author_shifts/ShiftActionsMenu'
import { useEventTimeZone } from '../author_shifts/ShiftAuthoringContext'
import { PublishLink } from '../publish_roster/PublishLink'
import { isPendingRemoval } from '../shifts/publishState'

import type { ShiftWithSlots } from '../shifts/types'

const t = scopedTranslation('roster_board')

type RosterShiftRowProps = {
  shift: ShiftWithSlots
  onChange: () => void
}

export function RosterShiftRow({ shift, onChange }: RosterShiftRowProps) {
  const timeZone = useEventTimeZone()
  const pendingRemoval = isPendingRemoval(shift)
  const selected = useShiftsStore((state) => state.selectedShiftIds.has(shift.id))
  const toggleShift = useShiftsStore((state) => state.toggleShift)

  return (
    <div
      data-testid="roster-shift-row"
      className={cn('tw:flex tw:items-start tw:gap-3 tw:px-4 tw:py-3', pendingRemoval ? 'tw:bg-gray-50' : 'tw:hover:bg-gray-50')}
    >
      <label
        className={cn(
          'tw:flex tw:shrink-0 tw:cursor-pointer tw:items-center tw:self-stretch tw:border-r tw:px-3.5 tw:-my-3 tw:-ml-4',
          selected ? 'tw:border-primary-200 tw:bg-primary-100' : 'tw:border-gray-100 tw:hover:bg-gray-50'
        )}
      >
        <SelectionCheckbox
          checked={selected}
          onChange={() => toggleShift(shift.id)}
          aria-label={t('bulk.select_shift', { role: shift.roleName })}
        />
      </label>
      <div className="tw:flex tw:w-48 tw:shrink-0 tw:flex-col tw:gap-1">
        <div className={cn('tw:font-semibold tw:text-gray-900', pendingRemoval && 'tw:text-gray-500 tw:line-through')}>
          {shift.roleName}
        </div>
        <div
          className={cn(
            'tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-gray-500',
            pendingRemoval && 'tw:line-through'
          )}
        >
          <Icon type="clock" className="tw:h-3 tw:w-3" aria-hidden />
          {timeFormatter.timeRange(shift.startTime, shift.endTime, timeZone)}
        </div>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
          <ShiftStatusBadge
            publishState={shift.publishState}
            changeBreakdown={shift.changeBreakdown}
            roleName={shift.roleName}
          />
          {!shift.allowSelfSelect && <AssignOnlyBadge />}
        </div>
      </div>
      <div className={cn('tw:shrink-0', pendingRemoval && 'tw:opacity-50')}>
        <CoverageMeter filled={shift.shiftSlots.assignedCount} total={shift.shiftSlots.totalCount} />
      </div>
      <div className="tw:flex-1">
        {pendingRemoval ? (
          <PendingRemovalAssignees shift={shift} />
        ) : (
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <SlotChips slots={shift.shiftSlots.nodes ?? []} publishState={shift.publishState} onChange={onChange} />
            <RemovedRow shiftId={shift.id} assignees={shift.removedAssignees ?? []} onChange={onChange} />
          </div>
        )}
      </div>
      <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
        {pendingRemoval ? (
          <UndoRemovalButton shiftId={shift.id} onChange={onChange} />
        ) : (
          <>
            <PublishLink shift={shift} />
            <ShiftActionsMenu shift={shift} />
          </>
        )}
      </div>
    </div>
  )
}
